import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const fileUrl = (d) => `/qc-submissions/${d.entity_id}/files/${d.id}/download`;
const showable = (d) => d.content_type !== "image/heic";

const blobs = new Map();
const loadBlob = (d) => {
  if (!blobs.has(d.id)) {
    blobs.set(d.id, api.get(fileUrl(d), { responseType: "blob" }).then((r) => URL.createObjectURL(r.data)).catch(() => { blobs.delete(d.id); return null; }));
  }
  return blobs.get(d.id);
};

function useBlobUrl(d) {
  const [src, setSrc] = useState(null);
  const ok = showable(d);
  useEffect(() => {
    if (!ok) { return undefined; }
    let live = true;
    loadBlob(d).then((u) => { if (live) setSrc(u); });
    return () => { live = false; };
  }, [d, ok]);
  return src;
}

function Thumb({ d, onOpen }) {
  const src = useBlobUrl(d);
  return (
    <button type="button" onClick={() => onOpen(d)} className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100"
      title={`${d.original_filename} · ${d.scope}`} data-testid={`qc-photo-thumb-${d.id}`}>
      {src ? <img src={src} alt={d.original_filename} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
        : <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-[10px] text-slate-500"><Camera className="h-5 w-5" />{showable(d) ? "Loading…" : "HEIC"}</span>}
      <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1 py-0.5 text-[10px] text-white">{d.scope}</span>
    </button>
  );
}

function PhotoViewer({ d, onClose }) {
  const src = useBlobUrl(d);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl w-[95vw]" data-testid="qc-photo-viewer">
        <DialogHeader><DialogTitle className="text-sm">{d.original_filename}</DialogTitle></DialogHeader>
        {src ? <img src={src} alt={d.original_filename} className="max-h-[65vh] w-full rounded object-contain bg-slate-50" data-testid="qc-photo-viewer-img" />
          : <p className="text-xs text-slate-500">{showable(d) ? "Loading…" : "HEIC pictures can't be previewed in the browser - download to view."}</p>}
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <StatusBadge status={d.qc_status} /><span>{d.scope}</span><span>· {d.uploaded_by?.name} · {fmtDateTime(d.uploaded_at)}</span>
          <Button size="sm" variant="outline" className="ml-auto" data-testid="qc-photo-viewer-download"
            onClick={() => downloadFile(fileUrl(d), d.stored_name).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4 mr-1" />Download</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Gallery({ lmId, refreshKey }) {
  const { data, reload } = useFetch(`/qc-submissions/photos?lm_id=${lmId}`);
  const [open, setOpen] = useState(null);
  useEffect(() => { if (refreshKey) reload(); }, [refreshKey, reload]);
  if (!data?.length) return null;
  return (
    <section className="mb-4 rounded-md border border-slate-200 bg-white p-3" data-testid="qc-photo-gallery">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><Camera className="h-4 w-4" />QC pictures <span className="font-normal text-slate-500">({data.length})</span></div>
      <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-2">{data.map((d) => <Thumb key={d.id} d={d} onOpen={setOpen} />)}</div>
      {open && <PhotoViewer d={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

export function QcPhotoGallery({ lmId, refreshKey }) {
  const { can } = useMe();
  return can("qc.view") ? <Gallery lmId={lmId} refreshKey={refreshKey} /> : null;
}
